import React, { useState } from 'react';
import axios from 'axios';

export interface EditAILimitTarget {
  user_id: number;
  name: string;
  // Current effective limit shown as the placeholder value when there's no override yet.
  limit_usd: number;
  has_override: boolean;
}

interface Props {
  target: EditAILimitTarget;
  onClose: () => void;
  /** Called after a successful save with the server's response (new spend snapshot). */
  onSaved: (updated: { user_id: number; limit_usd: number; has_override: boolean }) => void;
}

/** Sets or clears one user's monthly AI spend override. Used from the admin Users
 *  table — the caller decides where to mount it and what target to show. */
const EditAILimitModal: React.FC<Props> = ({ target, onClose, onSaved }) => {
  const [value, setValue] = useState<string>(
    target.has_override ? target.limit_usd.toFixed(2) : ''
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    const trimmed = value.trim();
    const limit = trimmed === '' ? null : Number(trimmed);
    if (limit !== null && !Number.isFinite(limit)) {
      setError('Enter a valid dollar amount, or leave blank to clear.');
      return;
    }
    if (limit !== null && limit < 0) {
      setError('Limit must be zero or positive.');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.patch(`/users/${target.user_id}/ai-limit`, {
        ai_monthly_limit_usd: limit,
      });
      onSaved(response.data);
    } catch {
      setError('Could not save the limit. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="ai-limit-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={() => !saving && onClose()}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Edit AI limit
        </h3>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Per-month spend cap for <strong>{target.name}</strong>. Leave blank to clear the
          override and fall back to the global default.
        </p>
        {error && (
          <div
            role="alert"
            className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
          >
            {error}
          </div>
        )}
        <label className="block text-sm text-gray-700 dark:text-gray-200">
          <span>Monthly limit (USD)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            placeholder="(use default)"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAILimitModal;
